import { useState } from "react";
import { AlertTriangle, Check, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deleteProblem, restoreProblem, type ProblemTarget } from "./problem-list";

type Props = {
  target: ProblemTarget | null;
  onClose: () => void;
  onDeleted: (target: ProblemTarget) => void;
  onRestored: (target: ProblemTarget) => void;
};

/** Mount with `key={target?.id}` so each problem starts at the confirmation. */
export function DeleteProblemDialog({ target, onClose, onDeleted, onRestored }: Props) {
  const [busy, setBusy] = useState(false);
  const [alreadyDeleted, setAlreadyDeleted] = useState<boolean | null>(null);
  const [error, setError] = useState("");

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      setAlreadyDeleted(await deleteProblem(target, onDeleted));
    } catch (err) {
      setError(err instanceof Error ? err.message : "The problem could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      await restoreProblem(target, onRestored);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The problem could not be restored.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => !open && !busy && onClose()}
    >
      <DialogContent className="sm:max-w-lg">
        <DeleteProblemPanel
          problemId={target?.id ?? null}
          busy={busy}
          alreadyDeleted={alreadyDeleted}
          error={error}
          onClose={onClose}
          onConfirm={() => void confirm()}
          onRestore={() => void restore()}
        />
      </DialogContent>
    </Dialog>
  );
}

type PanelProps = {
  problemId: string | null;
  busy: boolean;
  alreadyDeleted: boolean | null;
  error: string;
  onClose: () => void;
  onConfirm: () => void;
  onRestore: () => void;
};

export function DeleteProblemPanel({
  problemId,
  busy,
  alreadyDeleted,
  error,
  onClose,
  onConfirm,
  onRestore,
}: PanelProps) {
  const done = alreadyDeleted !== null;
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          {done ? (
            <Check className="h-5 w-5 text-emerald-600" strokeWidth={2} />
          ) : (
            <AlertTriangle className="h-5 w-5 text-destructive" strokeWidth={2} />
          )}
          <DialogTitle className="text-xl">
            {!done
              ? "Delete this problem?"
              : alreadyDeleted
                ? "Already deleted"
                : "Problem deleted"}
          </DialogTitle>
        </div>
        <DialogDescription>
          <span className="font-mono font-semibold text-foreground">{problemId}</span>
          {!done
            ? " will no longer appear in bank lookups, practice selection or these lists."
            : alreadyDeleted
              ? " had already been deleted. Nothing changed."
              : " is deleted from the question bank."}
        </DialogDescription>
      </DialogHeader>

      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Lessons that already use this problem keep their own copy and need
        their own correction. Variants made from it stay in the bank.
      </p>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <DialogFooter>
        {done ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={onRestore}
              disabled={busy}
              className="rounded-lg px-6"
            >
              Restore
            </Button>
            <Button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-6">
              Close
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg px-6"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirm}
              disabled={busy}
              className="gap-2 rounded-lg px-6"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {busy ? "Deleting…" : "Delete problem"}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}
